import {
  ConnectedOverlayPositionChange,
  ConnectedPosition,
  Overlay,
  ScrollStrategy,
} from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  Output,
} from '@angular/core';

type PopoverPosition = 'top' | 'bottom' | 'left' | 'right';

const POSITION_MAP: Record<PopoverPosition, ConnectedPosition[]> = {
  top: [
    { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -8 },
    { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 8 },
  ],
  bottom: [
    { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 8 },
    { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -8 },
  ],
  left: [
    { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -8 },
    { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 8 },
  ],
  right: [
    { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 8 },
    { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -8 },
  ],
};

@Component({
  selector: 'sgt-confirm-popover',
  templateUrl: './confirm-popover.component.html',
  styleUrls: ['./confirm-popover.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmPopoverComponent implements OnDestroy {
  @Input() message = 'Tem certeza?';
  @Input() confirmLabel = 'Sim';
  @Input() cancelLabel = 'Não';
  @Input() variant: 'danger' | 'primary' = 'danger';

  @Input() set position(value: PopoverPosition) {
    this._position = value;
    this.overlayPositions = POSITION_MAP[value] ?? POSITION_MAP.left;
    this.arrowClass = `confirm-popover--${value}`;
  }
  get position(): PopoverPosition {
    return this._position;
  }

  @Output() confirmed = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  isOpen = false;
  overlayPositions: ConnectedPosition[] = POSITION_MAP.left;
  arrowClass = 'confirm-popover--left';
  readonly overlayPanelClass = 'sgt-confirm-popover-overlay-panel';
  readonly scrollStrategy: ScrollStrategy;

  private _position: PopoverPosition = 'left';
  private readonly onScroll = (): void => this.zone.run(() => this.close());

  constructor(
    private overlay: Overlay,
    private cdr: ChangeDetectorRef,
    private zone: NgZone,
  ) {
    this.scrollStrategy = this.overlay.scrollStrategies.close();
  }

  ngOnDestroy(): void {
    this.unbindScroll();
  }

  open(): void {
    this.isOpen = true;
    this.zone.runOutsideAngular(() =>
      window.addEventListener('scroll', this.onScroll, true),
    );
    this.cdr.markForCheck();
  }

  close(): void {
    this.isOpen = false;
    this.unbindScroll();
    this.cdr.markForCheck();
  }

  private unbindScroll(): void {
    window.removeEventListener('scroll', this.onScroll, true);
  }

  onPositionChange(change: ConnectedOverlayPositionChange): void {
    const { overlayX, overlayY } = change.connectionPair;
    let side: PopoverPosition;
    if (overlayY === 'top') {
      side = 'bottom';
    } else if (overlayY === 'bottom') {
      side = 'top';
    } else if (overlayX === 'start') {
      side = 'right';
    } else {
      side = 'left';
    }
    this.arrowClass = `confirm-popover--${side}`;
    this.cdr.markForCheck();
  }

  onConfirm(): void {
    this.confirmed.emit();
    this.close();
  }

  onCancel(): void {
    this.cancelled.emit();
    this.close();
  }
}
